import React, { useEffect, useRef, useState } from 'react';
import { Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import { io } from 'socket.io-client';
import 'xterm/css/xterm.css';
import deploymentConfig from '../config/deployment';

const ClaudeTerminal = ({ onClose }) => {
  const terminalRef = useRef(null);
  const xtermRef = useRef(null);
  const socketRef = useRef(null);
  const fitAddonRef = useRef(null);
  const [status, setStatus] = useState('connecting'); // connecting, connected, disconnected, error
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    // Ensure the terminal element exists and has dimensions
    if (!terminalRef.current) {
      return;
    }

    // Wait for element to be fully rendered with dimensions
    const waitForElement = () => {
      return new Promise((resolve) => {
        const checkDimensions = () => {
          const rect = terminalRef.current?.getBoundingClientRect();
          if (rect && rect.width > 0 && rect.height > 0) {
            resolve();
          } else {
            requestAnimationFrame(checkDimensions);
          }
        };
        checkDimensions();
      });
    };

    // Initialize terminal after element is ready
    const initTerminal = async () => {
      await waitForElement();

      // Initialize xterm.js
      const term = new Terminal({
        cursorBlink: true,
        fontSize: 14,
        fontFamily: 'Menlo, Monaco, "Courier New", monospace',
        theme: {
          background: '#1e1e1e',
          foreground: '#d4d4d4',
          cursor: '#ffffff',
          black: '#000000',
          red: '#cd3131',
          green: '#0dbc79',
          yellow: '#e5e510',
          blue: '#2472c8',
          magenta: '#bc3fbc',
          cyan: '#11a8cd',
          white: '#e5e5e5',
          brightBlack: '#666666',
          brightRed: '#f14c4c',
          brightGreen: '#23d18b',
          brightYellow: '#f5f543',
          brightBlue: '#3b8eea',
          brightMagenta: '#d670d6',
          brightCyan: '#29b8db',
          brightWhite: '#e5e5e5'
        },
        rows: 30,
        cols: 100
      });

      const fitAddon = new FitAddon();
      term.loadAddon(fitAddon);

      term.open(terminalRef.current);

      // Fit after a small delay to ensure renderer is ready
      setTimeout(() => {
        try {
          fitAddon.fit();
        } catch (error) {
          console.error('[Terminal] Initial fit error:', error);
          // Retry once after another delay
          setTimeout(() => {
            try {
              fitAddon.fit();
            } catch (e) {
              console.error('[Terminal] Retry fit error:', e);
            }
          }, 200);
        }
      }, 50);

      xtermRef.current = term;
      fitAddonRef.current = fitAddon;

      // Connect to Socket.IO - remove /api suffix for WebSocket connection
      const token = localStorage.getItem('cms_accessToken') || localStorage.getItem('cmsToken');
      const socketUrl = deploymentConfig.apiBaseUrl.replace(/\/api$/, '');

      const socket = io(socketUrl, {
        auth: { token },
        transports: ['websocket', 'polling']
      });

      socketRef.current = socket;

      // Socket.IO event handlers
      socket.on('connect', () => {
        console.log('[Terminal] Connected to server');
        setStatus('connected');
        term.writeln('\r\n\x1b[32m✓ Connected to terminal\x1b[0m');
        term.writeln('\x1b[33mType your commands below. Session will timeout after 15 minutes of inactivity.\x1b[0m\r\n');
      });

      socket.on('terminal:init', ({ workingDirectory }) => {
        console.log('[Terminal] Initialized with working directory:', workingDirectory);
        term.writeln(`\x1b[36mWorking directory: ${workingDirectory}\x1b[0m\r\n`);
      });

      socket.on('connect_error', (error) => {
        console.error('[Terminal] Connection error:', error);
        setStatus('error');
        setErrorMessage(error.message);
        term.writeln(`\r\n\x1b[31m✗ Connection error: ${error.message}\x1b[0m\r\n`);
      });

      socket.on('disconnect', (reason) => {
        console.log('[Terminal] Disconnected:', reason);
        setStatus('disconnected');
        term.writeln(`\r\n\x1b[33m○ Disconnected: ${reason}\x1b[0m\r\n`);
      });

      socket.on('terminal:data', (data) => {
        term.write(data);
      });

      socket.on('terminal:error', (message) => {
        term.writeln(`\r\n\x1b[31mError: ${message}\x1b[0m\r\n`);
      });

      socket.on('terminal:exit', ({ exitCode }) => {
        term.writeln(`\r\n\x1b[33mTerminal process exited with code ${exitCode}\x1b[0m\r\n`);
      });

      // Handle terminal input
      term.onData((data) => {
        if (socket.connected) {
          socket.emit('terminal:input', data);
        }
      });

      // Handle window resize
      const handleResize = () => {
        try {
          fitAddon.fit();
          const { cols, rows } = term;
          if (socket.connected) {
            socket.emit('terminal:resize', { cols, rows });
          }
        } catch (error) {
          console.error('[Terminal] Resize error:', error);
        }
      };

      window.addEventListener('resize', handleResize);

      // Cleanup
      return () => {
        window.removeEventListener('resize', handleResize);
        if (socket) {
          socket.disconnect();
        }
        if (term) {
          try {
            term.dispose();
          } catch (error) {
            console.error('[Terminal] Disposal error:', error);
          }
        }
      };
    };

    // Start initialization
    initTerminal();
  }, []);

  const handleClose = () => {
    if (socketRef.current) {
      socketRef.current.disconnect();
    }
    if (onClose) {
      onClose();
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'connected': return 'bg-green-500';
      case 'connecting': return 'bg-yellow-500';
      case 'disconnected': return 'bg-gray-500';
      case 'error': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'connected': return 'Connected';
      case 'connecting': return 'Connecting...';
      case 'disconnected': return 'Disconnected';
      case 'error': return `Error: ${errorMessage}`;
      default: return 'Unknown';
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-900">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-800 border-b border-gray-700">
        <div className="flex items-center space-x-3">
          <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <h2 className="text-lg font-semibold text-white">Terminal</h2>
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${getStatusColor()} animate-pulse`}></div>
            <span className="text-sm text-gray-300">{getStatusText()}</span>
          </div>
        </div>
        <button
          onClick={handleClose}
          className="text-gray-400 hover:text-white transition-colors"
          title="Close terminal"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Terminal Container */}
      <div className="flex-1 p-4 overflow-hidden">
        <div
          ref={terminalRef}
          className="h-full w-full rounded-lg overflow-hidden"
          style={{ backgroundColor: '#1e1e1e' }}
        />
      </div>

      {/* Footer */}
      <div className="px-4 py-2 bg-gray-800 border-t border-gray-700">
        <div className="flex items-center justify-between text-xs text-gray-400">
          <div className="flex items-center space-x-4">
            <span>Session will timeout after 15 minutes of inactivity</span>
          </div>
          <div className="flex items-center space-x-2">
            <span>Terminal CLI</span>
            <span>•</span>
            <span>Press Ctrl+C to interrupt</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClaudeTerminal;
